// gloss:file

export const version = '0.0.1';

export {
  API_TOKEN_SCOPE_LABELS,
  API_TOKEN_SCOPES,
  type ApiTokenCapability,
  type ApiTokenScope,
  hasApiTokenCapability,
} from './apiTokens/scopes';
export {
  CLAIMS_NS,
  type CreateZealotTokenInput,
  createZealotTokenPayload,
  decodeTokenPayload,
  isZealotEmbedToken,
  isZealotSessionIssuer,
  parseZealotTokenClaims,
  type SpoofContext,
  ZEALOT_SESSION_TOKEN_ISSUERS,
  ZEALOT_TOKEN_AUDIENCE,
  ZEALOT_TOKEN_EXPIRY_SECONDS,
  ZEALOT_TOKEN_ISSUER,
  type ZealotEmbedTokenPayload,
  type ZealotTokenClaims,
} from './auth/zealotToken';
export { copyToClipboard, getClipboardData } from './browser/clipboard';
export { safeLocalStorage, safeSessionStorage } from './browser/safeStorage';
export {
  ACTIVITY_TYPES,
  type ActivityType,
  CURRENCIES,
  type Currency,
  DEFAULT_CURRENCY_CODE,
  FIELD_ICON_NAMES,
  type FieldIconName,
  firstPasswordHint,
  getCurrency,
  getDefaultCurrency,
  getMissionTypeID,
  getMissionTypeName,
  getPlatformId,
  isCustomMissionMission,
  isFieldIconName,
  isOneWayBlastMission,
  isReferenceCallMission,
  isReferenceOptInMission,
  isReviewMission,
  isSubmitUGCMission,
  isSurveyMission,
  isValidMissionType,
  isValidRedemptionPeriod,
  isValidRewardType,
  isVideoTestimonialMission,
  JOIN_SOURCE_LABELS,
  JOIN_SOURCE_UNKNOWN_LABEL,
  type JoinSource,
  MAX_PAGE_SIZE,
  MAX_REDEMPTIONS_PER_USER_CAP,
  MISSION_TYPE_IDS,
  MISSION_TYPES,
  type MissionType,
  type MissionTypeID,
  normalizeMissionType,
  PASSWORD_POLICY_HINT,
  PASSWORD_RULES,
  PLATFORM_ALIASES,
  PLATFORM_LABELS,
  type PlatformId,
  REDEMPTION_PERIOD_DAYS,
  REDEMPTION_PERIODS,
  REWARD_TYPES,
  type RedemptionPeriod,
  type RewardType,
  SURVEY_ANSWERS_MAX_PAGE_SIZE,
  supportsSurveyQuestions,
  validatePassword,
} from './constants';
export {
  APPLICATION_FIELD_TYPES,
  type ApplicationFieldType,
  isSelectFieldType,
  isTextLikeFieldType,
  normalizeLegacyTextType,
} from './constants/applicationFieldTypes';
export {
  CUSTOM_FIELD_TARGET_MODELS,
  type CustomFieldTargetModel,
  DEFAULT_CUSTOM_FIELD_TARGET_MODEL,
} from './customFields/targetModels';
export {
  getApiErrorMessage,
  isApiError,
  type ParsedApiError,
  parseApiError,
  type StandardApiError,
} from './errors';
export { linkifyHtml } from './forum/linkifyHtml';
export { normalizeTabContent } from './forum/normalizeTabContent';
export { POLL_MAX_OPTIONS } from './forum/pollLimits';
export { sanitizeCommentHtml } from './forum/sanitizeCommentHtml';
export { FORUM_RICH_TEXT_ATTRS, FORUM_RICH_TEXT_TAGS, FORUM_SANITIZE_CONFIG } from './forum/sanitizeConfig';
export { sanitizeForumPostHtml } from './forum/sanitizeForumPostHtml';
export {
  COMPLETION_REDIRECT_URL_MAX,
  isAllowedCompletionRedirectUrl,
  resolveCompletionRedirectUrl,
} from './missions/completionRedirect';
export { getEditParamsAudienceType } from './missions/editParamsAudienceType';
export {
  compareNewestFirst,
  type FanLastMissionRow,
  isLastMissionEligible,
  type LastMissionCandidate,
  type LastMissionPointer,
  type LastMissionRepair,
  type LastMissionRepairPlan,
  type LastMissionRow,
  pickLastFanMission,
  planLastFanMissionRepairs,
} from './missions/lastMissionEligibility';
export { hasMissionStarted, isMissionAvailableNow, isMissionExpired } from './missions/missionAvailability';
export { classifySubmission, FAN_MISSION_APPROVAL, type SubmissionReviewState } from './missions/submissionReviewState';
export {
  type ByPageRequest,
  fetchAllByCursor,
  fetchAllByPage,
  IncompletePageWalkError,
  iterateByCursor,
  iterateByPage,
  PAGINATION_WIRE_NAMES,
  RepeatedCursorError,
} from './pagination';
export {
  buildSurveyFileAccept,
  DEFAULT_SURVEY_FILE_TYPES,
  parseSurveyFileAnswer,
  SURVEY_FILE_TYPE_OPTIONS,
  type SurveyFileAnswer,
  type SurveyFileType,
  serializeSurveyFileAnswer,
} from './survey/fileAnswer';
export { decodeBase64Url } from './utils/base64url';
export { cleanUrlParam } from './utils/cleanUrlParam';
export { collectionToDictionary } from './utils/collectionToDictionary';
export { compareText } from './utils/compareText';
export { accountDomainFromEmail, inferCompanyNameFromDomain, isFreeEmailDomain, normalizeDomain } from './utils/domain';
export { isCloudFrontVideo, isEmbeddableUrl, isSafeUrl, normalizeEmbedUrl } from './utils/embedUrl';
export { escapeRegExp } from './utils/escapeRegExp';
export {
  buildAnswersByQuestionNumber,
  type ConditionalLogic,
  type ConditionalLogicRule,
  evaluateConditionalLogic,
} from './utils/evaluateConditionalLogic';
export { type FetchTokenFn, fetchTokenWithRetry, NULL_TOKEN_RETRY_MS } from './utils/fetchTokenWithRetry';
export { firstQueryParam } from './utils/firstQueryParam';
export { foldText, foldTextNoPad } from './utils/foldText';
export { type FuzzyContainsOptions, fuzzyContains, levenshteinDistance, maxFuzzyDistance } from './utils/fuzzyMatch';
export { is2xx, is3xx, is4xx, is5xx, isErrorStatus } from './utils/httpStatus';
export { isNonEmptyArray } from './utils/isNonEmptyArray';
export { isRecord } from './utils/isRecord';
export {
  buildBrandPortalBaseUrl,
  normalizeBrandSubdomain,
  resolveBrandIdentifierStem,
  resolveBrandPortalBaseUrl,
} from './utils/normalizeBrandSubdomain';
export { EMPTY_ONBOARDING_OVERVIEW_COPY, parseOnboardingOverviewCopy } from './utils/onboardingOverviewCopy';
export {
  type MissionContent,
  type OneClickPostConfig,
  type ParsedQuestion,
  parseMissionContent,
} from './utils/parseMissionContent';
export { POLL_REQUEST_HEADER, pollRequestHeaders } from './utils/pollRequestHeaders';
export {
  formatQuestionFromUrlParams,
  formatSurveyQuestionFromApi,
  formatTestimonialQuestionFromApi,
  type NormalizedQuestion,
  type SurveyQuestion,
  type TestimonialQuestion,
} from './utils/questionFormatting';
export {
  type GuardProspectEmailArgs,
  guardProspectEmail,
  looksLikeSalesforceContactId,
  looksLikeSalesforceOpportunityId,
  parseSalesforceOpportunityRef,
  type SalesforceOpportunityRef,
  sanitizeProspectEmail,
} from './utils/salesforceId';
export { createSingleFlight, type SingleFlight } from './utils/singleFlight';
export { stripHtml } from './utils/stripHtml';
export {
  COMMON_TIME_ZONES,
  convertTo24Hour,
  convertToLocalTimeZone,
  DEFAULT_TIMEZONE,
  DEFAULT_TIMEZONE_ABBR,
  type EventWindow,
  formatDateTime,
  formatDateTimeShort,
  formatEventTimeRange,
  formatHourSlot,
  formatLocalDay,
  formatTimeRange,
  formatTimezoneAbbr,
  formatUtcDay,
  getBrowserTimezone,
  getEventWindow,
  getFriendlyTimeZoneName,
  getNowInTimezone,
  groupSlotsByDate,
  isEventInProgress,
  isEventPast,
  normalizeTimeZone,
  parseDateOnly,
  pickedDayToInstant,
  pickedDayToUtc,
  safeParseDate,
  TIMEZONE_OPTIONS,
  utcToZonedCalendarDate,
  utcToZonedHour,
  utcToZonedWallClock,
  type ZonedWallClock,
  zonedWallClockToUtc,
  zonedWallClockToUtcIso,
} from './utils/timezone';
export {
  findLegacyComment,
  humanizeTimeAgo,
  type LegacyCommentShape,
  type LegacyPostShape,
  nestLegacyComments,
  translateV2CommentToLegacyShape,
  translateV2PostToLegacyShape,
} from './utils/v2PostTranslator';
export { EMPTY_WELCOME_MODAL_COPY, parseWelcomeModalCopy, type WelcomeModalCopy } from './utils/welcomeModalCopy';
export { EMAIL_REGEX, isValidEmail } from './validation/email';
